import { type HreflangVariant } from '@cx-web/api-clients/hreflang-api';
import { buildLinkedDataSchema } from '@cx-web/utils';

import { type PageMetadata } from '~/types/template';

import type { Route } from './+types/route';

/** Block dangerous URL protocols. Returns null to signal the entry should be skipped.
 * Root URLs (pathname === '/') are normalised to origin-only (no trailing slash) to match CF worker behaviour (OCGC-2602).
 */
function sanitizeHreflangUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Safe: checking against malicious protocols to block them, not executing
    // eslint-disable-next-line no-script-url
    if (['javascript:', 'data:', 'vbscript:'].includes(parsed.protocol)) {
      return null;
    }
    // Strip trailing slash from root URLs: https://www.oddschecker.com/ → https://www.oddschecker.com
    if (parsed.pathname === '/' && !parsed.search && !parsed.hash) {
      return parsed.origin;
    }
    return url;
  } catch {
    return null;
  }
}

export const meta: Route.MetaFunction = ({ matches: [_root, dynamicRoute] }) => {
  const pageMetadata = dynamicRoute.data.pageMetadata as PageMetadata;
  const metaTitle = pageMetadata.metaTitle;
  const metaDescription = pageMetadata.metaDescription;
  const hreflangLinks = pageMetadata.hreflangLinks ?? [];
  const linkedDataSchema = buildLinkedDataSchema(pageMetadata.linkedDataSchema);
  const paginationMeta = dynamicRoute.data.paginationMeta;
  const requestUrl = dynamicRoute.data.url;
  // canonicalUrlOverride wins over the hreflangLinks-derived canonical below, unless the page is paginated.
  const canonicalUrlOverride = pageMetadata.canonicalUrl;
  const robotsDirectives = [pageMetadata.noIndex && 'noindex', pageMetadata.noFollow && 'nofollow'].filter(
    Boolean,
  ) as string[];

  const isPaginatedPage = Object.values(dynamicRoute.data.sections ?? {}).some(item =>
    item?.find(y => (y?.componentArgs as { pagination?: string })?.pagination === 'BOTTOM_PAGE_NUMBERS'),
  );

  // Use hreflang API data when available; fall back to template API links on null.
  const hreflangData = dynamicRoute.data.hreflangData as HreflangVariant[] | null;
  const useApiData = hreflangData && hreflangData.length > 0;

  // Generate pagination link tags
  const paginationLinkTags: any[] = [];
  let filteredHreflangLinks = hreflangLinks;

  if (isPaginatedPage && paginationMeta && requestUrl) {
    const url = new URL(requestUrl);
    url.protocol = 'https';

    // Filter out existing canonical tag from hreflangLinks when we have pagination (only needed for fallback)
    if (!useApiData) {
      filteredHreflangLinks = hreflangLinks.filter(link => link.rel !== 'canonical');
    }

    // Self-referencing canonical
    const canonicalUrl = new URL(url);
    if (paginationMeta.currentPage === 1) {
      // Page 1 should have clean URL without articlesPage parameter
      canonicalUrl.searchParams.delete('articlesPage');
    } else {
      canonicalUrl.searchParams.set('articlesPage', String(paginationMeta.currentPage));
    }
    paginationLinkTags.push({
      tagName: 'link',
      rel: 'canonical',
      href: canonicalUrl.toString(),
    });

    // Prev link (only if not on first page)
    if (paginationMeta.hasPrevPage) {
      const prevUrl = new URL(url);
      const prevPage = paginationMeta.currentPage - 1;
      if (prevPage === 1) {
        // Link to page 1 should have clean URL without articlesPage parameter
        prevUrl.searchParams.delete('articlesPage');
      } else {
        prevUrl.searchParams.set('articlesPage', String(prevPage));
      }
      paginationLinkTags.push({
        tagName: 'link',
        rel: 'prev',
        href: prevUrl.toString(),
      });
    }

    // Next link (only if not on last page)
    if (paginationMeta.hasNextPage) {
      const nextUrl = new URL(url);
      nextUrl.searchParams.set('articlesPage', String(paginationMeta.currentPage + 1));
      paginationLinkTags.push({
        tagName: 'link',
        rel: 'next',
        href: nextUrl.toString(),
      });
    }
  }

  // Drop the hreflangLinks-derived canonical when the override wins, to avoid emitting two
  // conflicting canonical tags.
  if (!useApiData && !isPaginatedPage && canonicalUrlOverride) {
    filteredHreflangLinks = filteredHreflangLinks.filter(link => link.rel !== 'canonical');
  }

  if (!useApiData) {
    filteredHreflangLinks = filteredHreflangLinks.map(link => ({
      ...link,
      href: link.href.replace('http:', 'https:'),
    }));
  }

  const canonicalOverrideLinkTag =
    canonicalUrlOverride && !isPaginatedPage
      ? [{ tagName: 'link' as const, rel: 'canonical', href: canonicalUrlOverride }]
      : [];

  const robotsMetaTag = robotsDirectives.length > 0 ? [{ name: 'robots', content: robotsDirectives.join(', ') }] : [];

  const hreflangMetaTags = useApiData
    ? hreflangData.flatMap(({ locale, url: rawUrl }) => {
        const safeUrl = sanitizeHreflangUrl(rawUrl);
        if (!safeUrl) return [];
        const normalizedUrl = safeUrl.replace(/^http:/, 'https:');
        return [
          {
            tagName: 'link' as const,
            rel: 'alternate',
            hrefLang: locale,
            href: normalizedUrl,
            'data-cx-web-created': 'true',
          },
        ];
      })
    : (filteredHreflangLinks ?? []);

  return [
    { title: metaTitle },
    { name: 'description', content: metaDescription },
    ...robotsMetaTag,
    ...hreflangMetaTags,
    ...canonicalOverrideLinkTag,
    ...paginationLinkTags,
    ...(linkedDataSchema
      ? [
          {
            'script:ld+json': linkedDataSchema,
          },
        ]
      : []),
  ];
};
